/**
 * Owner statements — the frozen artifact, step 13. Insert-only (no UPDATE
 * grant to app_user at all — see schema.prisma's OwnerStatement doc
 * comment), so there is nothing to edit here, only read and download.
 */

import { useState } from 'react';
import { useList } from '@refinedev/core';
import type { OwnerStatementDto } from 'shared';
import { api } from '../../../lib/api.js';
import { DataTable, type Column } from '../../../components/DataTable.js';

export function OwnerStatementListPage() {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data,
    isLoading,
    error: listError,
  } = useList<OwnerStatementDto>({
    resource: 'owner-statements',
    pagination: { mode: 'off' },
  });

  async function download(row: OwnerStatementDto) {
    setBusyId(row.id);
    setError(null);
    try {
      const { downloadUrl } = await api<{ downloadUrl: string }>(`/owner-statements/${row.id}/pdf`);
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open that statement.');
    } finally {
      setBusyId(null);
    }
  }

  const columns: Column<OwnerStatementDto>[] = [
    { key: 'owner', header: 'Owner', render: (row) => row.ownerName },
    { key: 'period', header: 'Period', render: (row) => `${row.periodStart} – ${row.periodEnd}` },
    { key: 'net', header: 'Net', numeric: true, render: (row) => `$${row.netAmount}` },
    {
      key: 'balance',
      header: 'Balance after',
      numeric: true,
      render: (row) => `$${row.balanceAfter}`,
    },
    {
      key: 'download',
      header: '',
      render: (row) => (
        <button
          type="button"
          className="button button--quiet"
          disabled={busyId === row.id}
          onClick={() => void download(row)}
        >
          {busyId === row.id ? 'Opening…' : 'Download PDF'}
        </button>
      ),
    },
  ];

  return (
    <section className="stack">
      <h1>Owner Statements</h1>
      <p className="muted">
        Frozen at generation — a dispute reversing a payment later shows up as a negative line on a
        future statement, never a rewrite of one already sent.
      </p>
      {error ? <p className="alert alert--error">{error}</p> : null}
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={listError}
        emptyMessage="No statements generated yet."
      />
    </section>
  );
}
